import React, { Suspense, useEffect, useRef, useState } from "react"
import { AppLayout } from "app/core/layouts/AppLayout"
import { BlitzPage } from "@blitzjs/next"
import { getAntiCSRFToken } from "@blitzjs/auth"
import Loading from "app/core/components/Loading"
import { ClientOnly } from "app/core/components/ClientOnly"
import { PageHeader } from "app/core/components/PageHeader"

import { TestResultsList } from "app/test-results/components/TestResultsList"
import { TestFluencyResultsList } from "app/test-results/components/TestFluencyResultsList"

import { Button } from "app/core/components/Button"
import Spinner from "react-md-spinner"
import { toast } from "react-hot-toast"
import clsx from "clsx"
import { ButtonLink } from "app/core/components/ButtonLink"
import { SearchTests } from "app/test-results/components/TestSeachResults"
import { IoMdSchool } from "react-icons/io"

type ProgressStudent = {
  studentId: number
  studentName: string
  grade?: string | null
  termName?: string | null
  schoolYear?: string
}

type TabsProps = {
  admin?: boolean
  terms?: {}
  students?: {}
  progressStudents?: ProgressStudent[]
}

export const TabsComponent = ({
  admin = false,
  terms = {},
  students = {},
  progressStudents,
}: TabsProps) => {
  const [selectedLists, setSelectedLists] = useState("Growth")
  const selectedTerms = Object.keys(terms).filter((term) => terms[term])
  const selectedStudentIds = Object.keys(students)
    .filter((student) => students[student])
    .map((id) => parseInt(id, 10))

  return (
    <div>
      <div className="space-y-0.25">
        <div className="flex w-full bg-gray-100 rounded-lg p-1 gap-2">
          <button
            onClick={() => setSelectedLists("Growth")}
            type="button"
            className={clsx(
              "flex-1 text-center py-2 px-8 text-base font-medium rounded-md " +
                "transition-shadow transition-transform transform duration-200 focus:outline-none",
              selectedLists === "Growth"
                ? "bg-white text-primary-600 shadow-lg -translate-y-0.25 border border-gray-50"
                : "bg-white text-gray-600 shadow-md border border-transparent " +
                    "hover:shadow-lg hover:-translate-y-0.5 hover:border-gray-50"
            )}
          >
            MAP Growth Results
          </button>

          <button
            onClick={() => setSelectedLists("Fluency")}
            type="button"
            className={clsx(
              "flex-1 text-center py-2 px-8 text-base font-medium rounded-md " +
                "transition-shadow transition-transform duration-200 transform focus:outline-none",
              selectedLists === "Fluency"
                ? "bg-white text-primary-600 shadow-lg -translate-y-0.25 border border-gray-50"
                : "bg-white text-gray-600 shadow-md border border-transparent " +
                    "hover:shadow-lg hover:-translate-y-0.5 hover:border-gray-50"
            )}
          >
            MAP Fluency Results
          </button>

          {progressStudents && (
            <button
              onClick={() => setSelectedLists("Progress")}
              type="button"
              className={clsx(
                "flex-1 text-center py-2 px-8 text-base font-medium rounded-md " +
                  "transition-shadow transition-transform duration-200 transform focus:outline-none",
                selectedLists === "Progress"
                  ? "bg-white text-primary-600 shadow-lg -translate-y-0.25 border border-gray-50"
                  : "bg-white text-gray-600 shadow-md border border-transparent " +
                      "hover:shadow-lg hover:-translate-y-0.5 hover:border-gray-50"
              )}
            >
              Progress Reports
            </button>
          )}
        </div>

        {selectedLists === "Growth" ? (
          <ClientOnly fallback={<Loading />}>
            <TestResultsList admin={admin} terms={selectedTerms} studentIds={selectedStudentIds} />
          </ClientOnly>
        ) : selectedLists === "Fluency" ? (
          <ClientOnly fallback={<Loading />}>
            <TestFluencyResultsList admin={admin} />
          </ClientOnly>
        ) : progressStudents ? (
          <div className="space-y-4">
            {progressStudents.length === 0 ? (
              <div className="text-gray-500 py-4 px-4">
                No students with progress reports found.
              </div>
            ) : (
              <div className="overflow-hidden bg-white shadow sm:rounded-md">
                {Object.entries(
                  progressStudents.reduce<Record<string, ProgressStudent[]>>((acc, r) => {
                    const year = r.schoolYear ?? r.termName ?? "Unknown"
                    ;(acc[year] ??= []).push(r)
                    return acc
                  }, {})
                )
                  .sort(([a], [b]) => b.localeCompare(a))
                  .map(([year, students]) => (
                    <div key={year}>
                      <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-sm font-semibold text-gray-600">
                        {year}
                      </div>
                      <ul>
                        {students.map((r, index) => (
                          <li
                            key={`${r.studentId}-${year}`}
                            className={clsx(index > 0 && "border-t border-gray-200")}
                          >
                            <a
                              href={`/reports/progress/${r.studentId}${
                                r.schoolYear ? `?schoolYear=${r.schoolYear}` : ""
                              }`}
                              className="block hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition duration-150 ease-in-out"
                            >
                              <div className="flex items-center px-4 py-4 sm:px-6">
                                <div className="flex-1 min-w-0 space-y-1">
                                  <div className="text-lg font-semibold text-gray-900 truncate leading-5">
                                    {r.studentName}
                                  </div>
                                  {r.grade && (
                                    <div className="flex items-center text-sm text-gray-600 leading-5 space-x-2">
                                      <IoMdSchool />
                                      <span>Grade {r.grade}</span>
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <svg
                                    className="w-5 h-5 text-gray-400"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </div>
                              </div>
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  )
}

const AdminTestResultsPage: BlitzPage = () => {
  const [loading, setLoading] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const fluencyFileRef = useRef<HTMLInputElement>(null)
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => () => abortRef.current?.abort(), [])

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || !files.length || !fileRef.current) return

    abortRef.current?.abort()
    abortRef.current = new AbortController()
    setLoading(true)

    const data = new FormData()
    data.append("file", files[0])
    const options: RequestInit = {
      method: "POST",
      body: data,
      headers: {},
      signal: abortRef.current.signal,
    }
    const antiCSRFToken = getAntiCSRFToken()
    if (antiCSRFToken) (options.headers as Record<string, string>)["anti-csrf"] = antiCSRFToken
    try {
      const result = await fetch("/api/test-results/uploadTestResult", options)

      if (!result.ok) {
        const errorData = await result.text()
        console.error("Upload failed. Raw response text:", errorData)

        try {
          const errorText = JSON.parse(errorData)
          throw new Error(errorText.error || "Unknown error occurred")
        } catch (jsonErr) {
          console.error("Failed to parse error JSON:", jsonErr)
          throw new Error("Unexpected response from server. Check logs.")
        }
      }

      fileRef.current.value = ""
      setLoading(false)
      toast.success(
        "Upload received — processing in the background. You'll get an email when it's done."
      )
    } catch (error) {
      if (error.name === "AbortError") return
      console.log("error ", error)
      setLoading(false)
      toast.error(`Error uploading test results: ${error.toString()}`)
    }
  }

  const handleFluencyUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || !files.length || !fluencyFileRef.current) return

    abortRef.current?.abort()
    abortRef.current = new AbortController()
    setLoading(true)

    const data = new FormData()
    data.append("file", files[0])
    const options: RequestInit = {
      method: "POST",
      body: data,
      headers: {},
      signal: abortRef.current.signal,
    }
    const antiCSRFToken = getAntiCSRFToken()
    if (antiCSRFToken) (options.headers as Record<string, string>)["anti-csrf"] = antiCSRFToken
    try {
      const result = await fetch("/api/test-results/uploadFluencyTestResult", options)

      if (!result.ok) {
        const errorData = await result.text()
        try {
          const errorJson = JSON.parse(errorData)
          throw new Error(errorJson.error || "Unknown error occurred")
        } catch (jsonErr) {
          throw new Error("Unexpected response from server. Check logs.")
        }
      }

      fluencyFileRef.current.value = ""
      setLoading(false)
      toast.success("Upload received — processing in the background.")
    } catch (error) {
      if (error.name === "AbortError") return
      setLoading(false)
      toast.error(`Error uploading test results: ${error.toString()}`)
    }
  }
  const buttonTextClass = loading ? "invisible" : "visible"
  const spinnerClass = loading ? "visible" : "invisible"

  return (
    <div className="md:py-6 px-4 pt-2 pb-6 sm:px-6 lg:px-8 space-y-4">
      <div className="flex justify-between items-center">
        <PageHeader title="Admin Test Results" />
        <div className="flex">
          <div className="mr-2">
            <input type="file" ref={fileRef} className="hidden" onChange={handleUpload} />
            <Button
              type="button"
              onClick={(e) => fileRef.current && fileRef.current.click()}
              className="relative"
            >
              <span className={buttonTextClass}>Upload NWEA CSV</span>
              <div className="absolute left-1/2 transform -translate-x-1/2">
                <Spinner className={spinnerClass} size={26} singleColor="#ffffff" />
              </div>
            </Button>
          </div>
          <div className="mr-2">
            <input
              type="file"
              ref={fluencyFileRef}
              className="hidden"
              onChange={handleFluencyUpload}
            />
            <Button
              type="button"
              onClick={(e) => fluencyFileRef.current && fluencyFileRef.current.click()}
              className="relative"
            >
              <span className={buttonTextClass}>Upload NWEA Fluency CSV</span>
              <div className="absolute left-1/2 transform -translate-x-1/2">
                <Spinner className={spinnerClass} size={26} singleColor="#ffffff" />
              </div>
            </Button>
          </div>
          <div>
            <ButtonLink href="/admin/test-results/progress">Test Completion Tracking</ButtonLink>
          </div>
        </div>
      </div>
      <div className="mt-3 mb-3 flex justify-end">
        <SearchTests resetPage={true} />
      </div>
      <Suspense fallback={<Loading />}>
        <TabsComponent admin={true} />
      </Suspense>
    </div>
  )
}

AdminTestResultsPage.authenticate = true
AdminTestResultsPage.getLayout = (page) => <AppLayout>{page}</AppLayout>

export default AdminTestResultsPage
